import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, XCircle } from 'lucide-react';
import useDisclosure from '../../hooks/useDisclosure';
import { useToast } from '../../context/ToastContext';
import documentService from '../../services/documentService';
import folderService from '../../services/folderService';
import { triggerDownload } from '../../utils/download';
import ShareDialog from './ShareDialog';
import NewFolderDialog from './NewFolderDialog';
import MoveDialog from './MoveDialog';
import ReplaceDialog from './ReplaceDialog';
import EditDetailsDialog from './EditDetailsDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import Drawer from '../common/Drawer';
import VersionHistory from './VersionHistory';
import ActivityFeed from './ActivityFeed';
import PinDialog from './PinDialog';

/**
 * Single source of document behaviour. Every list screen gets the same actions, the same
 * dialogs and the same audit-producing calls, so behaviour cannot drift between screens.
 *
 * Returns { actions, dialogs } — render `dialogs` once in the page.
 */
export default function useDocumentActions({ reload, onOpenFolder } = {}) {
  const navigate = useNavigate();
  const toast = useToast();

  const share = useDisclosure();
  const rename = useDisclosure();
  const move = useDisclosure();
  const copy = useDisclosure();
  const replace = useDisclosure();
  const edit = useDisclosure();
  const confirmDelete = useDisclosure();
  const confirmPurge = useDisclosure();
  const versions = useDisclosure();
  const activity = useDisclosure();
  const pin = useDisclosure();

  const refresh = useCallback(() => reload?.(), [reload]);

  const actions = useMemo(
    () => ({
      /** Open the pin dialog. Documents only — a folder is not a thing to announce. */
      pin(item) {
        pin.open(item);
      },

      open(item) {
        if (item.type === 'folder') {
          if (onOpenFolder) onOpenFolder(item);
          else navigate(`/documents/${item.id}`);
        } else {
          navigate(`/documents/${item.id}/preview`);
        }
      },

      /** Mint a short-lived signed URL and start the download. Audited server-side. */
      async download(item) {
        try {
          const { url } = await documentService.downloadUrl(item.id);
          if (url) {
            triggerDownload(url, item.name);
            toast.success(`Downloading "${item.name}"`);
          } else {
            // Mock mode has no bytes to serve; the audit and UX path still runs.
            toast.success(`Downloading "${item.name}" — logged to the audit trail`);
          }
        } catch (err) {
          toast.error(err.message || 'The document could not be downloaded.');
        }
      },

      share: (item) => share.open({ item, tab: 'people' }),
      externalLink: (item) => share.open({ item, tab: 'link' }),
      rename: (item) => rename.open(item),
      move: (item) => move.open(item),
      copy: (item) => copy.open(item),
      replace: (item) => replace.open(item),
      editDetails: (item) => edit.open(item),
      versions: (item) => versions.open(item),
      activity: (item) => activity.open(item),
      remove: (item) => confirmDelete.open(item),
      purge: (item) => confirmPurge.open(item),

      async toggleFavorite(item) {
        try {
          const result = await documentService.toggleFavorite(item.type === 'folder' ? 'folder' : 'document', item.id);
          toast.success(result.isFavorite ? 'Added to favourites' : 'Removed from favourites');
          refresh();
        } catch (err) {
          toast.error(err.message || 'Could not update favourites.');
        }
      },

      async restore(item) {
        try {
          await documentService.restore(item.id);
          toast.success(`"${item.name}" restored`);
          refresh();
        } catch (err) {
          toast.error(err.message || 'The document could not be restored.');
        }
      },
    }),
    [navigate, toast, share, rename, move, copy, replace, edit, versions, activity, confirmDelete, confirmPurge, refresh, onOpenFolder],
  );

  const dialogs = (
    <>
      <PinDialog open={pin.isOpen} onClose={pin.close} document={pin.payload} onChanged={refresh} />

      <ShareDialog
        open={share.isOpen}
        onClose={share.close}
        document={share.payload?.item}
        initialTab={share.payload?.tab || 'people'}
        onChanged={refresh}
      />

      <NewFolderDialog
        open={rename.isOpen}
        onClose={rename.close}
        mode="rename"
        itemType={rename.payload?.type === 'folder' ? 'folder' : 'document'}
        initialName={rename.payload?.name || ''}
        onSubmit={async (name) => {
          const item = rename.payload;
          if (item.type === 'folder') await folderService.rename(item.id, name);
          else await documentService.update(item.id, { name });
          toast.success('Renamed');
          refresh();
        }}
      />

      <MoveDialog
        open={move.isOpen}
        onClose={move.close}
        item={move.payload}
        title="Move to"
        confirmLabel="Move here"
        onSubmit={async (folderId) => {
          const item = move.payload;
          // Folders and documents move through different endpoints; a folder move also
          // re-paths its whole subtree server-side.
          if (item.type === 'folder') await folderService.move(item.id, folderId);
          else await documentService.move(item.id, folderId);
          toast.success(`"${item.name}" moved`);
          refresh();
        }}
      />

      <MoveDialog
        open={copy.isOpen}
        onClose={copy.close}
        item={copy.payload}
        title="Copy to"
        confirmLabel="Copy here"
        onSubmit={async (folderId) => {
          await documentService.copy(copy.payload.id, folderId);
          toast.success(`Copy of "${copy.payload.name}" created`);
          refresh();
        }}
      />

      <ReplaceDialog open={replace.isOpen} onClose={replace.close} document={replace.payload} />

      <EditDetailsDialog
        open={edit.isOpen}
        onClose={edit.close}
        document={edit.payload}
        onSaved={() => {
          toast.success('Details saved');
          refresh();
        }}
      />

      <ConfirmDialog
        open={confirmDelete.isOpen}
        onClose={confirmDelete.close}
        destructive
        icon={Trash2}
        title={confirmDelete.payload?.type === 'folder' ? 'Delete folder?' : 'Delete document?'}
        message={
          confirmDelete.payload?.type === 'folder'
            ? `"${confirmDelete.payload?.name}" and everything inside it will be moved to Trash.`
            : `"${confirmDelete.payload?.name}" will be moved to Trash.`
        }
        detail="Items in Trash are recoverable for 30 days, then permanently removed."
        confirmLabel="Move to Trash"
        onConfirm={async () => {
          const item = confirmDelete.payload;
          try {
            if (item.type === 'folder') await folderService.remove(item.id);
            else await documentService.remove(item.id);
            toast.success(`"${item.name}" moved to Trash`, {
              action: {
                label: 'Undo',
                onClick: async () => {
                  await documentService.restore(item.id);
                  toast.success('Restored');
                  refresh();
                },
              },
            });
            refresh();
          } catch (err) {
            toast.error(err.message || 'The item could not be deleted.');
          }
        }}
      />

      <ConfirmDialog
        open={confirmPurge.isOpen}
        onClose={confirmPurge.close}
        destructive
        icon={XCircle}
        title="Delete permanently?"
        message={`"${confirmPurge.payload?.name}" and all of its versions will be erased from Inditech Document Hub.`}
        detail="This action cannot be undone."
        confirmText="DELETE"
        confirmLabel="Delete permanently"
        onConfirm={async () => {
          try {
            await documentService.purge(confirmPurge.payload.id);
            toast.success('Permanently deleted');
            refresh();
          } catch (err) {
            toast.error(err.message || 'The document could not be deleted.');
          }
        }}
      />

      <Drawer open={versions.isOpen} onClose={versions.close} title="Version history" subtitle={versions.payload?.name} width={420}>
        {versions.payload && <VersionHistory document={versions.payload} onChanged={refresh} framed={false} />}
      </Drawer>

      <Drawer open={activity.isOpen} onClose={activity.close} title="Activity" subtitle={activity.payload?.name} width={420}>
        {activity.payload && <ActivityFeed documentId={activity.payload.id} limit={40} framed={false} />}
      </Drawer>
    </>
  );

  return { actions, dialogs };
}
